// Radha — reporting reads + writes (migration 055): historical snapshots for
// trend charts, DSO, and the digest subscription/send flow.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";
import { edgeFunctionErrorMessage } from "../lib/edgeFunctionError";

export interface ReceivablesSnapshot {
  id: string; snapshot_date: string; total_outstanding: string; overdue_amount: string;
  current_amount: string; bucket_1_30: string; bucket_31_60: string; bucket_61_90: string;
  bucket_90_plus: string; account_count: number; avg_dpd: string; dso_days: string | null;
}

export function useSnapshots(limit = 90) {
  return useQuery({
    queryKey: ["receivables-snapshots", limit],
    queryFn: async (): Promise<ReceivablesSnapshot[]> => {
      const { data, error } = await supabase
        .from("receivables_snapshots")
        .select("id, snapshot_date, total_outstanding, overdue_amount, current_amount, bucket_1_30, bucket_31_60, bucket_61_90, bucket_90_plus, account_count, avg_dpd, dso_days")
        .order("snapshot_date", { ascending: true })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 30_000,
  });
}

export function useTakeSnapshot() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("snapshot_my_receivables");
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["receivables-snapshots"] }),
  });
}

export interface DigestSubscription {
  id: string; recipient_email: string; is_active: boolean; last_sent_at: string | null;
}

export function useDigestSubscriptions() {
  return useQuery({
    queryKey: ["digest-subscriptions"],
    queryFn: async (): Promise<DigestSubscription[]> => {
      const { data, error } = await supabase
        .from("digest_subscriptions")
        .select("id, recipient_email, is_active, last_sent_at")
        .order("recipient_email", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 15_000,
  });
}

export function useAddDigestSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (recipient_email: string) => {
      const { error } = await supabase.from("digest_subscriptions").insert({ recipient_email });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["digest-subscriptions"] }),
  });
}

export function useRemoveDigestSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("digest_subscriptions").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["digest-subscriptions"] }),
  });
}

export function usePreviewDigest() {
  return useMutation({
    mutationFn: async (): Promise<string> => {
      const { data, error } = await supabase.rpc("preview_my_digest");
      if (error) throw error;
      return data as string;
    },
  });
}

export function useSendDigestNow() {
  return useMutation({
    mutationFn: async (): Promise<{ sent: number; total: number; failures: string[] }> => {
      const { data, error } = await supabase.functions.invoke("send-digest-email", { body: {} });
      if (error) throw new Error(await edgeFunctionErrorMessage(error));
      if (data?.error) throw new Error(data.error);
      return data;
    },
  });
}

export function reportingErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  const m = msg.match(/^([A-Z_]+):\s*(.*)$/);
  return m ? m[2] || m[1] : msg;
}
